import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { CheckCircle } from 'lucide-react';
import { useDarkMode } from '../contexts/DarkModeContext';

const MAX_MSG_LEN = 50;
const NOTIFICATION_TIMEOUT_MS = 12000;

const truncate = (text) =>
  text && text.length > MAX_MSG_LEN ? text.slice(0, MAX_MSG_LEN).trimEnd() + '…' : text || '';

// Single notification pill — slides down from navbar edge on mount, fades out on dismiss
const NotificationItem = ({ id, userMessage, onClick, onDismiss }) => {
  const { isDarkMode } = useDarkMode();
  const [visible, setVisible] = useState(false);

  const dismiss = useCallback(() => {
    setVisible(false);
    setTimeout(() => onDismiss(id), 200);
  }, [id, onDismiss]);

  useEffect(() => {
    // Trigger enter animation on next frame
    const raf = requestAnimationFrame(() => setVisible(true));
    const timer = setTimeout(dismiss, NOTIFICATION_TIMEOUT_MS);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timer);
    };
  }, [dismiss]);

  const handleClick = () => {
    onClick();
    dismiss();
  };

  return (
    <div
      onClick={handleClick}
      className={`
        pointer-events-auto cursor-pointer
        flex items-center gap-2.5 px-4 py-2.5
        rounded-lg shadow-lg border
        text-sm select-none
        transition-all duration-200 ease-out
        hover:scale-[1.02] active:scale-[0.98]
        ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'}
        ${isDarkMode
          ? 'bg-[#1e293b] border-[#334155] text-gray-100 hover:bg-[#253347] hover:border-[#3d5068]'
          : 'bg-white border-gray-200 text-gray-800 hover:bg-gray-50 hover:border-gray-300'}
      `}
      style={{ maxWidth: '420px', minWidth: '220px' }}
    >
      <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
      <span className="truncate">
        <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Response ready — </span>
        <span className="font-medium">{truncate(userMessage)}</span>
      </span>
    </div>
  );
};

NotificationItem.propTypes = {
  id: PropTypes.string.isRequired,
  userMessage: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  onDismiss: PropTypes.func.isRequired,
};

// Container: fixed just below the navbar (56 px), right-aligned (mirrors ChatGPT placement)
const SessionNotification = ({ notifications, onDismiss }) => {
  if (!notifications.length) return null;

  return (
    <div
      className="fixed z-[9998] flex flex-col items-end gap-2 pointer-events-none"
      style={{ top: '64px', right: '24px' }}
    >
      {notifications.map((n) => (
        <NotificationItem
          key={n.id}
          id={n.id}
          userMessage={n.userMessage}
          onClick={n.onClick}
          onDismiss={onDismiss}
        />
      ))}
    </div>
  );
};

SessionNotification.propTypes = {
  notifications: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      userMessage: PropTypes.string.isRequired,
      onClick: PropTypes.func.isRequired,
    })
  ).isRequired,
  onDismiss: PropTypes.func.isRequired,
};

export default SessionNotification;
